/**
 * 会员购买（微信支付 · Native 扫码）。
 *
 * ## 为什么前端不写价格
 *
 * 后端 `core/pricing.py` 是唯一权威，`/api/pay/options` 把档位与金额一起给过来。
 * `FALLBACK_PRICES` 只在**那个请求失败时**用（断网、后端刚重启），
 * 而且必须与后端逐字一致 —— 出现不一致说明有人改了价没改这里，
 * 表现是"页面显示 45，付的时候变 54"，那是最难解释的一类客诉。
 *
 * ## 支付是"两条腿"
 *
 * 扫码付完，微信会异步 POST `/api/pay/notify`；同时这里每 2 秒轮询
 * `/api/pay/orders/<id>`。前者保证"用户付完就切走"也不漏，后者保证
 * "回调丢了"也能在几秒内看到开通。两条路都在后端先向微信查单再核销，
 * 这里不需要（也无法）判断"是不是真的付了"。
 */
import { api } from './api'

export type PayPrice = {
	months: number
	/** 分。用于展示与"是否整数"判断，真正下单只传 months */
	price_fen: number
	/** 元 */
	price: number
	/** 折算到每月多少元（后端算好，前端不做除法） */
	per_month: number
	label: string
	note: string
	badge?: string
}

export type PayOrder = {
	id: string
	out_trade_no: string
	months: number
	amount_fen: number
	amount: number
	status: 'pending' | 'paid' | 'closed' | 'refunded'
	/** SVG data URL。后端没装二维码库时是空串 */
	qr: string
	/** 二维码原文，兜底展示 */
	code_url: string
	created_at: string | null
}

export type PayPlan = {
	plan_id: string
	plan_expires_at: string | null
	is_plus: boolean
}

/**
 * 兜底价目表 —— 与 `backend/core/pricing.py` 的 `PRICE_OPTIONS` 逐条对应。
 *
 * 只在 `/api/pay/options` 请求失败时使用。改价必须两边一起改；
 * 若哪天忘了改这里，最坏结果是"断网时显示旧价"，下单仍按后端的新价，
 * 不会多收钱（金额由后端算）。
 */
export const FALLBACK_PRICES: PayPrice[] = [
	{months: 1, price_fen: 900, price: 9, per_month: 9, label: '1 个月', note: '按月试水'},
	{months: 6, price_fen: 4500, price: 45, per_month: 7.5, label: '半年', note: '省 9 元，等于送一个月', badge: '常见'},
	{months: 12, price_fen: 9000, price: 90, per_month: 7.5, label: '一年', note: '省 18 元，等于送两个月'},
]

/** 档位的推荐默认值：半年（大多数人的选择），找不到就取第一档 */
export const DEFAULT_MONTHS = 6

export async function fetchPayOptions(): Promise<{prices: PayPrice[], enabled: boolean}> {
	try {
		const data = await api.get<{prices: PayPrice[], pay_enabled: boolean}>('/pay/options')
		const prices = Array.isArray(data?.prices) && data.prices.length ? data.prices : FALLBACK_PRICES
		return {prices, enabled: Boolean(data?.pay_enabled)}
	} catch {
		// 拿不到就按"能付"处理并退到兜底价 —— 真不能付时，下单会返回 503，
		// 那时再给出"暂未开通"的提示，比一进来就吓唬人要好。
		return {prices: FALLBACK_PRICES, enabled: true}
	}
}

export async function createOrder(months: number): Promise<PayOrder> {
	const data = await api.post<{order: PayOrder}>('/pay/orders', {months})
	return data.order
}

export async function fetchOrder(id: string): Promise<{order: PayOrder, plan: PayPlan}> {
	return api.get<{order: PayOrder, plan: PayPlan}>(`/pay/orders/${id}`)
}

/**
 * 是不是在微信内置浏览器里打开。
 *
 * 只影响一句提示：微信里扫不了自己屏幕上的码，得让它"长按保存 → 扫一扫 →
 * 相册"。iPad / Mac 微信的 UA 也带 MicroMessenger，命中了也无妨 ——
 * 那句提示只是引导，不是硬约束。
 */
export function isWechatBrowser(): boolean {
	const ua = navigator.userAgent.toLowerCase()
	return ua.includes('micromessenger')
}

/** `2026-11-30` —— 到期时间只显示到天，算到时分没有意义（到点那天就停了） */
export function fmtExpiry(iso: string | null | undefined): string {
	if (!iso) return ''
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return ''
	const pad = (n: number) => String(n).padStart(2, '0')
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
